<template>
    <div>
        <Loader :loader="isPageLoading" />
        <div v-show="!isPageLoading">
            <b-form
                @submit.prevent="onSubmit"
                @reset.prevent="onReset"
                novalidate
            >
                <b-form-group
                    :label="`${$t('form.label.title')}*`"
                    label-for="title"
                >
                    <b-form-input
                        id="title"
                        ref="title"
                        type="text"
                        :placeholder="$t('form.placeholder.title')"
                        :state="chkState('title')"
                        v-model.lazy.trim="$v.form.title.$model"
                        aria-describedby="titleFeedback"
                        autocomplete="given-range"
                        autofocus
                    />
                    <b-form-invalid-feedback id="titleFeedback">
                        <span v-if="!$v.form.title.required">
                            {{ $t('form.validations.required') }}
                        </span>
                        <span v-if="!$v.form.title.maxLength">
                            {{
                                $t('form.validations.maxLength', {
                                    value: '255',
                                })
                            }}
                        </span>
                    </b-form-invalid-feedback>
                </b-form-group>
                <b-form-group
                    :label="`${$t('form.label.target')}*`"
                    label-for="target"
                >
                    <b-form-input
                        id="target"
                        ref="target"
                        type="text"
                        :placeholder="$t('form.placeholder.target')"
                        :state="chkState('target')"
                        v-model="$v.form.target.$model"
                        aria-describedby="target-feedback"
                    />
                    <b-form-invalid-feedback id="target-feedback">
                        <span v-if="!$v.form.target.required">
                            {{ $t('form.validations.required') }}
                        </span>
                        <span v-if="!$v.form.target.decimal">
                            {{ $t('form.validations.decimal') }}
                        </span>
                    </b-form-invalid-feedback>
                </b-form-group>
                <b-form-group
                    v-if="isGol"
                    :label="`${$t('form.label.multiplier')}*`"
                    label-for="multiplier"
                >
                    <b-form-input
                        id="multiplier"
                        ref="multiplier"
                        type="text"
                        :placeholder="$t('form.placeholder.multiplier')"
                        :state="chkState('multiplier')"
                        v-model="$v.form.multiplier.$model"
                        aria-describedby="multiplier-feedback"
                    />
                    <b-form-invalid-feedback id="multiplier-feedback">
                        <span v-if="!$v.form.multiplier.required">
                            {{ $t('form.validations.required') }}
                        </span>
                        <span v-if="!$v.form.multiplier.decimal">
                            {{ $t('form.validations.decimal') }}
                        </span>
                    </b-form-invalid-feedback>
                </b-form-group>
                <b-form-group
                    :label="`${$t('form.label.headlight')}*`"
                    label-for="headlight"
                >
                    <Select
                        id="headlight"
                        :options="headlights"
                        :state="chkState('headlight')"
                        @changeValue="onChangeHeadlight"
                        aria-describedby="headlightFeedback"
                        :selected="$v.form.headlight.$model"
                    />
                    <b-form-invalid-feedback id="headlightFeedback">
                        <span v-if="!$v.form.headlight.required">
                            {{ $t('form.validations.required') }}
                        </span>
                    </b-form-invalid-feedback>
                </b-form-group>
                <ButtonLoader
                    class="float-right ml-1"
                    type="submit"
                    variant="primary"
                    :disabled="$v.form.$invalid || loader"
                    :loader="loader"
                >
                    {{ $t('form.button.save') }}
                </ButtonLoader>
                <b-button
                    class="float-right"
                    variant="secondary"
                    :disabled="loader"
                    @click="redirectToList"
                >
                    {{ $t('form.button.cancel') }}
                </b-button>
            </b-form>
        </div>
    </div>
</template>

<script>
import api from '@/services/api'
import { validationMixin } from 'vuelidate'
import { required, maxLength, decimal } from 'vuelidate/lib/validators'
import ButtonLoader from '@/views/components/button-loader'
import {
    Select,
    mapperToOptions,
    mapperToOption,
} from '@/views/components/selects'
import Loader from '@/views/components/loader'
import { mapperRangeToForm } from '../mappers/mapperRangeToForm'
import { mapperHeadlightsToSelect } from '../mappers/mapperHeadlightsToSelect'
import { mapperHeadlightToSelect } from '../mappers/mapperHeadlightToSelect'
import { isCompany } from '@/services/companies-custom-functions'

const initialState = {
    title: '',
    target: '',
    headlight: {},
    multiplier: 1,
}

export default {
    name: 'Form',
    components: {
        ButtonLoader,
        Select,
        Loader,
    },
    props: ['range', 'rangePromise'],
    mixins: [validationMixin],
    data() {
        return {
            isGol: isCompany('gol') || isCompany('testegol'),
            form: Object.assign({}, initialState),
            loader: false,
            headlights: [],
            headlightReady: false,
        }
    },
    validations: {
        form: {
            title: {
                required,
                maxLength: maxLength(255),
            },
            headlight: {
                required,
            },
            target: {
                required,
                decimal,
            },
            multiplier: {
                required,
                decimal,
            },
        },
    },
    watch: {
        isPageLoading() {
            this.rangePromise &&
                this.rangePromise.then(() => {
                    const newFormInitialState = mapperRangeToForm(
                        this.range,
                        mapperToOption,
                        mapperHeadlightToSelect
                    )
                    this.form = this.getNewFormState(newFormInitialState)
                })
        },
    },
    computed: {
        isValid() {
            return !this.$v.form.$anyError
        },
        isDirty() {
            return this.$v.form.$anyDirty
        },
        isPageLoading() {
            return !this.headlightReady
        },
    },
    methods: {
        getHeadlightsFromAPI() {
            api.get('/headlight', {
                params: {
                    all: '1',
                    orderBy: 'title',
                    ascending: 1,
                },
            })
                .then(resp => {
                    const {
                        data: { data },
                    } = resp
                    const headlightsToSelect = mapperHeadlightsToSelect(data)
                    this.headlights = mapperToOptions(headlightsToSelect)
                })
                .catch(console.log)
                .finally(() => {
                    this.headlightReady = true
                })
        },
        onChangeHeadlight(values) {
            this.$v.form.headlight.$model = values
        },
        getNewFormState(obj) {
            return Object.assign({}, obj)
        },
        onSubmit() {
            if (this.validate()) {
                this.$nextTick(() => {
                    this.$emit(
                        'onSubmit',
                        this.form,
                        this.onReset,
                        this.isLoading
                    )
                })
            }
        },
        isLoading(state) {
            this.loader = state
        },
        onReset() {
            this.form = Object.assign({}, initialState)
            this.$nextTick(() => {
                this.$v.$reset()
            })
        },
        chkState(val) {
            const field = this.$v.form[val]
            return !field.$dirty || !field.$invalid
        },
        validate() {
            this.$v.$touch()
            return this.isValid
        },
        redirectToList() {
            this.$router.push({
                path: '/v2/target/ranges/list',
            })
        },
    },
    mounted() {
        this.getHeadlightsFromAPI()
    },
}
</script>

<style scoped>
.btn.disabled {
    cursor: auto;
}
</style>
